import { forwardRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { cn } from "@/lib/utils";

/* ----------------------------- Button ----------------------------- */
const base =
  "font-ui uppercase tracking-[0.22em] text-[0.72rem] font-medium inline-flex items-center justify-center gap-2 transition-all duration-500 ease-latir select-none disabled:opacity-40 disabled:pointer-events-none";

const variants = {
  primary:
    "bg-latir-red text-paper-raised px-8 py-4 hover:bg-latir-red-deep active:translate-y-[1px] rounded-[2px]",
  outline:
    "border border-ink text-ink px-8 py-4 hover:border-latir-red hover:text-latir-red rounded-[2px] bg-transparent font-semibold",
  reservar:
    "bg-paper-raised text-latir-red border border-latir-red px-7 py-3.5 hover:bg-latir-red hover:text-paper-raised rounded-[2px]",
  heroGhost:
    "text-white border border-white/60 px-8 py-4 hover:bg-white hover:text-ink hover:border-white rounded-[2px] bg-transparent backdrop-blur-[2px]",
  ghost:
    "text-latir-red px-1 py-1 hover:text-latir-red-deep bg-transparent",
  quiet:
    "text-ink px-1 py-1 hover:text-latir-red bg-transparent tracking-[0.24em]",
};

export const Button = forwardRef(function Button(
  { children, variant = "primary", to, href, arrow, className, ...props },
  ref
) {
  const cls = cn(base, variants[variant], className);
  const inner = (
    <>
      {arrow === "left" && <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />}
      {children}
      {arrow === "right" && <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />}
    </>
  );
  if (to) {
    return (
      <Link ref={ref} to={to} className={cls} {...props}>
        {inner}
      </Link>
    );
  }
  if (href) {
    return (
      <a ref={ref} href={href} className={cls} {...props}>
        {inner}
      </a>
    );
  }
  return (
    <button ref={ref} className={cls} {...props}>
      {inner}
    </button>
  );
});

/* --------------------------- SectionLabel -------------------------- */
export function SectionLabel({ children, index, className = "" }) {
  return (
    <div className={cn("flex items-center gap-4", className)}>
      {index && (
        <span className="font-ui text-[0.7rem] tracking-[0.2em] text-latir-red tabular-nums">
          {index}
        </span>
      )}
      <span className="h-px w-8 bg-latir-red/60" />
      <span className="eyebrow text-ink-mute">{children}</span>
    </div>
  );
}

/* ---------------------------- StatusBadge -------------------------- */
const statusStyles = {
  disponible: { dot: "bg-transparent border border-ink-mute", text: "text-ink-soft" },
  reservada: { dot: "bg-ink-mute/40 border border-ink-mute", text: "text-ink-soft" },
  vendida: { dot: "bg-latir-red border border-latir-red", text: "text-latir-red" },
};

export function StatusBadge({ status = "disponible", label, className = "", size = "md" }) {
  const s = statusStyles[status] || statusStyles.disponible;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-2 font-ui uppercase tracking-[0.2em]",
        size === "sm" ? "text-[0.6rem]" : "text-[0.68rem]",
        s.text,
        className
      )}
      data-status={status}
    >
      <span className={cn("inline-block rounded-full", size === "sm" ? "w-1.5 h-1.5" : "w-2 h-2", s.dot)} />
      {label}
    </span>
  );
}
